import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Info } from 'lucide-react';
import { merchantExperienceApi, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type {
  CategoryDto,
  ExperienceType,
  MerchantExperienceDto,
  MerchantExperienceRequestDto,
  PassPlanOptionDto,
} from '@kardume/types';
import { EXPERIENCE_TYPE_LABELS, MERCHANT_EXPERIENCE_ERROR_MESSAGES } from '@kardume/types';
import {
  Button,
  CurrencyInput,
  Modal,
  SearchableComboBox,
  Select,
  Switch,
  TextInput,
} from '@kardume/ui';

/**
 * Cadastro e edição de uma experiência (card #347).
 *
 * Duas coisas que o formulário precisa DIZER, e não só fazer:
 *
 * 1. **Nasce em moderação.** A experiência salva não vai para a vitrine: vai
 *    para a fila do Kardume. Sem esse aviso o lojista procura na vitrine, não
 *    acha, e conclui que o cadastro falhou.
 * 2. **Editar uma recusada a reenvia.** O core devolve a experiência para a
 *    fila automaticamente ao salvar uma edição de algo `REJECTED` - quem está
 *    corrigindo precisa saber que é isso que o botão faz.
 */

const TITLE_MAX_LENGTH = 180;
const TEXT_MAX_LENGTH = 4000;

const Grid = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
  color: ${(p) => p.theme.textSecondary};
`;

const Textarea = styled.textarea<{ $error?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  min-height: 96px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  font-family: inherit;
  color: ${(p) => p.theme.textPrimary};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => (p.$error ? p.theme.error : p.theme.border)};
  border-radius: ${(p) => p.theme.radius};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
  }
`;

const Counter = styled.div`
  margin-top: 0.25rem;
  text-align: right;
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  color: ${(p) => p.theme.textMuted};
`;

const ToggleField = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  cursor: pointer;
`;

const HelpText = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textMuted};
`;

const Notice = styled.div`
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  padding: 0.75rem 0.9rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.83rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
    color: ${(p) => p.theme.primary};
  }
  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const TYPE_OPTIONS = (Object.keys(EXPERIENCE_TYPE_LABELS) as ExperienceType[]).map((value) => ({
  value,
  label: EXPERIENCE_TYPE_LABELS[value],
}));

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Nulo = criar. Preenchido = editar (descrição e regras vêm do detalhe). */
  experience: MerchantExperienceDto | null;
  categories: CategoryDto[];
  passPlans: PassPlanOptionDto[];
  onSaved: () => void;
}

interface FormState {
  title: string;
  type: ExperienceType;
  categoryId: string;
  description: string;
  rules: string;
  price: number | undefined;
  kardumeExclusive: boolean;
  requiredPassPlanId: string;
}

const EMPTY_FORM: FormState = {
  title: '',
  type: 'TASTING',
  categoryId: '',
  description: '',
  rules: '',
  price: undefined,
  kardumeExclusive: false,
  requiredPassPlanId: '',
};

const ExperienceFormModal: React.FC<Props> = ({
  isOpen,
  onClose,
  experience,
  categories,
  passPlans,
  onSaved,
}) => {
  const { showNotification } = useNotification();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const editingId = experience?.id ?? null;
  const wasRejected = experience?.status === 'REJECTED';

  /**
   * Descrição e regras só existem no detalhe - a lista não as traz. Carregar
   * aqui evita que a página precise buscar o detalhe só para abrir o editor, e
   * evita o pior: salvar um PUT com os dois campos vazios porque a tela nunca
   * os teve.
   */
  useEffect(() => {
    if (!isOpen) return;
    setSubmitted(false);
    if (!experience) {
      setForm(EMPTY_FORM);
      return;
    }
    setForm({
      title: experience.title,
      type: experience.type,
      categoryId: experience.categoryId ?? '',
      description: '',
      rules: '',
      price: experience.price > 0 ? experience.price : undefined,
      kardumeExclusive: experience.kardumeExclusive,
      requiredPassPlanId: experience.requiredPassPlanId ?? '',
    });
    setLoadingDetail(true);
    merchantExperienceApi
      .detail(experience.id)
      .then((detail) => {
        setForm((prev) => ({
          ...prev,
          description: detail.description ?? '',
          rules: detail.rules ?? '',
        }));
      })
      .catch((err) => {
        showNotification(
          extractErrorMessage(err, 'Não foi possível carregar a descrição desta experiência.'),
          'error'
        );
      })
      .finally(() => setLoadingDetail(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, experience?.id]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]): void =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const trimmedTitle = form.title.trim();
  const titleError = submitted && trimmedTitle === '' ? 'Informe o título da experiência.' : undefined;

  const submit = async (): Promise<void> => {
    setSubmitted(true);
    if (trimmedTitle === '') return;

    const payload: MerchantExperienceRequestDto = {
      title: trimmedTitle,
      type: form.type,
      categoryId: form.categoryId || undefined,
      description: form.description.trim() || undefined,
      rules: form.rules.trim() || undefined,
      // Campo vazio no CurrencyInput é `undefined`, e aqui isso significa
      // gratuita: o core deriva `free` de `price == 0`.
      price: form.price ?? 0,
      kardumeExclusive: form.kardumeExclusive,
      requiredPassPlanId: form.requiredPassPlanId || undefined,
    };

    setSubmitting(true);
    try {
      if (editingId) {
        await merchantExperienceApi.update(editingId, payload);
        showNotification(
          wasRejected
            ? 'Experiência atualizada e reenviada para a revisão do Kardume.'
            : 'Experiência atualizada.',
          'success'
        );
      } else {
        await merchantExperienceApi.create(payload);
        showNotification(
          'Experiência criada. Ela está aguardando a revisão do Kardume antes de aparecer na vitrine.',
          'success'
        );
      }
      onSaved();
      onClose();
    } catch (err) {
      showNotification(
        extractErrorMessage(
          err,
          'Não foi possível salvar a experiência.',
          MERCHANT_EXPERIENCE_ERROR_MESSAGES
        ),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingId ? 'Editar experiência' : 'Nova experiência'}
      width="720px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={submit} isLoading={submitting} disabled={loadingDetail}>
            {editingId ? 'Salvar' : 'Criar experiência'}
          </Button>
        </>
      }
    >
      <Grid>
        <Notice>
          <Info size={16} />
          {editingId ? (
            <span>
              {wasRejected ? (
                <>
                  <strong>Salvar reenvia para revisão.</strong> Esta experiência foi recusada;
                  ao salvar, ela volta para a fila do Kardume e é analisada de novo.
                </>
              ) : (
                <>
                  Mudar o título <strong>muda o endereço público</strong> da experiência. O endereço
                  antigo continua funcionando - não é erro.
                </>
              )}
            </span>
          ) : (
            <span>
              A experiência nasce <strong>aguardando revisão do Kardume</strong>. Ela só aparece na
              vitrine depois de aprovada - e só se tiver ao menos uma data futura marcada.
            </span>
          )}
        </Notice>

        <div>
          <TextInput
            label="Título"
            required
            value={form.title}
            maxLength={TITLE_MAX_LENGTH}
            error={titleError}
            placeholder="Ex.: Degustação de vinhos da casa"
            onChange={(e) => update('title', e.target.value)}
          />
          <Counter>
            {form.title.length}/{TITLE_MAX_LENGTH}
          </Counter>
        </div>

        <Row>
          <Select
            label="Formato"
            options={TYPE_OPTIONS}
            value={form.type}
            onChange={(e) => update('type', e.target.value as ExperienceType)}
          />
          <SearchableComboBox
            label="Categoria"
            items={categories.map((category) => ({ id: category.id, label: category.name }))}
            valueId={form.categoryId}
            placeholder="Escolha a categoria"
            noResultsText="Nenhuma categoria encontrada"
            onChange={(id) => update('categoryId', id)}
          />
        </Row>

        <div>
          <FieldLabel htmlFor="experience-description">Descrição</FieldLabel>
          <Textarea
            id="experience-description"
            value={form.description}
            maxLength={TEXT_MAX_LENGTH}
            placeholder="O que a pessoa vai viver: o que acontece, quanto tempo dura, o que está incluso."
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              update('description', e.target.value)
            }
          />
          <HelpText>
            É este texto que a moderação compara com o que a loja entrega. Prometer o que não
            acontece é o que faz a experiência ser recusada.
          </HelpText>
        </div>

        <div>
          <FieldLabel htmlFor="experience-rules">Regras</FieldLabel>
          <Textarea
            id="experience-rules"
            value={form.rules}
            maxLength={TEXT_MAX_LENGTH}
            placeholder="Ex.: proibido para menores de 18 anos; cancelamento até 24h antes; bebida não inclusa."
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => update('rules', e.target.value)}
          />
        </div>

        <Row>
          <div>
            <CurrencyInput
              label="Preço"
              value={form.price}
              onChange={(value) => update('price', value)}
            />
            <HelpText>Em branco ou zero = experiência gratuita.</HelpText>
          </div>
          <div>
            <Select
              label="Exclusiva para Kardume Pass"
              options={[
                { value: '', label: 'Aberta a todo mundo' },
                ...passPlans.map((plan) => ({ value: plan.id, label: `A partir de ${plan.name}` })),
              ]}
              value={form.requiredPassPlanId}
              onChange={(e) => update('requiredPassPlanId', e.target.value)}
            />
            <HelpText>
              Restringe quem pode reservar. Deixe aberta se a experiência vale para qualquer
              visitante.
            </HelpText>
          </div>
        </Row>

        <div>
          <ToggleField>
            <Switch
              checked={form.kardumeExclusive}
              onChange={(value) => update('kardumeExclusive', value)}
            />
            Exclusiva Kardume
          </ToggleField>
          <HelpText>
            Marque quando esta experiência só existe para quem chega pelo Kardume. O selo não promete
            qualidade - diz apenas que a oferta é exclusiva daqui.
          </HelpText>
        </div>
      </Grid>
    </Modal>
  );
};

export default ExperienceFormModal;
